import "@/index.css";
import "@/styles/hm-base.css";
import "@/styles/hm-app.css";
import { BrowserRouter, Navigate, Route, Routes } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { ThemeProvider } from "@/context/ThemeContext";
import { useMotion } from "@/hooks/useMotion";
import AppShell from "@/components/hm/AppLayout";
import Landing from "@/screens/Landing";
import { Register, Login, ForgotPassword, ResetPassword } from "@/screens/Auth";
import Dashboard from "@/screens/Dashboard";
import Academia from "@/screens/Academia";
import ModulePage from "@/screens/ModuleReader";
import Recursos from "@/screens/Recursos";
import Comunidad from "@/screens/Comunidad";
import Planes from "@/screens/Planes";
import { PaymentSuccess, PaymentCancel, Privacidad } from "@/screens/Misc";
import PublicModule from "@/screens/PublicModule";
import ResourceLanding from "@/screens/ResourceLanding";
import { UnderstandPage, PracticePage, WelcomeModulePage } from "@/screens/WelcomePages";
import ParentsPage from "@/screens/ParentsPage";
import AdminLayout from "@/admin/AdminLayout";
import AdminDashboard from "@/admin/Dashboard";
import AdminStudents from "@/admin/Students";
import StudentDetail from "@/admin/StudentDetail";
import AdminPayments from "@/admin/Payments";
import AdminContent from "@/admin/Content";
import ModuleEditor from "@/admin/ModuleEditor";
import AdminAccess from "@/admin/Access";
import AdminSettings from "@/admin/Settings";

const Loading = () => (
  <div style={{ minHeight: "100vh", display: "grid", placeItems: "center" }} data-testid="loading">
    <span className="h-3 w-3 animate-ping rounded-full" style={{ background: "var(--hm-accent)" }} />
  </div>
);

const Protected = ({ children }) => {
  const { user } = useAuth();
  if (user === null) return <Loading />;
  if (!user) return <Navigate to="/entrar" replace />;
  return children;
};

const AdminOnly = ({ children }) => {
  const { user } = useAuth();
  if (user === null) return <Loading />;
  if (!user) return <Navigate to="/entrar" replace />;
  if (user.role !== "admin") return <Navigate to="/inicio" replace />;
  return children;
};

const GuestOnly = ({ children }) => {
  const { user } = useAuth();
  if (user === null) return <Loading />;
  if (user) return <Navigate to="/inicio" replace />;
  return children;
};

export default function App() {
  useMotion();
  return (
    <ThemeProvider>
    <AuthProvider>
      <BrowserRouter>
        <Routes>
          <Route path="/" element={<Landing />} />
          <Route path="/registro" element={<GuestOnly><Register /></GuestOnly>} />
          <Route path="/entrar" element={<GuestOnly><Login /></GuestOnly>} />
          <Route path="/recuperar" element={<GuestOnly><ForgotPassword /></GuestOnly>} />
          <Route path="/restablecer" element={<ResetPassword />} />
          <Route path="/bienvenida" element={<PublicModule />} />
          <Route path="/modulo-bienvenida" element={<WelcomeModulePage />} />
          <Route path="/entender-lo-que-te-esta-pasando" element={<UnderstandPage />} />
          <Route path="/practica-guiada" element={<PracticePage />} />
          <Route path="/padres" element={<ParentsPage />} />
          <Route path="/bienvenida/entender" element={<Navigate to="/entender-lo-que-te-esta-pasando" replace />} />
          <Route path="/bienvenida/practica" element={<Navigate to="/practica-guiada" replace />} />
          <Route path="/como-funciona" element={<Landing />} />
          <Route path="/login" element={<GuestOnly><Login /></GuestOnly>} />
          <Route path="/recursos/:slug" element={<ResourceLanding />} />
          <Route path="/privacidad" element={<Privacidad />} />
          <Route element={<Protected><AppShell /></Protected>}>
            <Route path="/inicio" element={<Dashboard />} />
            <Route path="/academia" element={<Academia />} />
            <Route path="/academia/:id" element={<ModulePage />} />
            <Route path="/recursos" element={<Recursos />} />
            <Route path="/comunidad" element={<Comunidad />} />
            <Route path="/planes" element={<Planes />} />
            <Route path="/payment/success" element={<PaymentSuccess />} />
            <Route path="/payment/cancel" element={<PaymentCancel />} />
          </Route>
          <Route path="/admin" element={<AdminOnly><AdminLayout /></AdminOnly>}>
            <Route index element={<Navigate to="/admin/dashboard" replace />} />
            <Route path="dashboard" element={<AdminDashboard />} />
            <Route path="alumnos" element={<AdminStudents />} />
            <Route path="alumnos/:id" element={<StudentDetail />} />
            <Route path="pagos" element={<AdminPayments />} />
            <Route path="contenido" element={<AdminContent />} />
            <Route path="contenido/:id" element={<ModuleEditor />} />
            <Route path="accesos" element={<AdminAccess />} />
            <Route path="configuracion" element={<AdminSettings />} />
          </Route>
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </BrowserRouter>
      <Toaster position="top-center" />
    </AuthProvider>
    </ThemeProvider>
  );
}
